import { ResolverState } from '../resolvers/definition/resolverState';
import { Logger } from '../devtools/logger';
import { ResourceRegistry } from '../resources/resourceRegistry';
import { CacheEntry, createEmptyEntry } from './entries/cacheEntry';
import { ChangeContext, type ChangeManager } from '../client/changeManager';
import { setResourceErrorMeta } from '../client/errorMeta';
import type { ResourceInstanceState } from '../client/types';
import type { DevtoolsCallbacks } from '../devtools/maybeLoadDevtools';
import { timing } from '../metrics/timing';
import { Link } from '../links/definition/link';
import { calculateTags } from '../links/discovery/calculateTags';
import { parseTypeName } from '../resources/resource';
import { GroupManager } from '../groups/groupManager';
import { createGCManager } from './gcManager';
import { produceNextCacheEntry } from './entries/produceNextCacheEntry';
import { tryMarkStale } from './entries/tryMarkStale';
import { createLinkManager } from './linkManager';
import { PersistenceManager } from '../persistence/persistenceManager';
import { createNodeStore } from './nodeStore';

export { __testOnlyDisableCacheGC, __testOnlyEnableCacheGC } from './gcManager';

export type ChangeMeta = {
  dataChanged: boolean;
  flagsChanged: boolean;
};

export type StateEvent = {
  changeContext: ChangeContext;
} & ChangeMeta;

export type CollectEvent = {
  changeContext: ChangeContext;
};

export type PullEvent = {
  changeContext: ChangeContext;
  result: ChangeMeta;
};

export type InitEvent = {
  onInit: (parentInstanceKey: string) => void;
};

export type { NodeEvents, NodeEmitter } from './nodeStore';

type DeferredLifecycleEffect = () => void;

export const createResourceLifecycleManager = ({
  logger,
  resourceRegistry,
  groupManager,
  changeManager,
  onInvalidate,
  onStateChange,
  onNodeCollected,
  devtools,
  persistenceManager,
}: {
  logger?: Logger;
  resourceRegistry: ResourceRegistry;
  groupManager: GroupManager;
  changeManager: ChangeManager;
  onInvalidate: (args: {
    instanceKey: string;
    triggeringContext: ChangeContext;
  }) => void;
  onStateChange: (
    instanceKey: string,
    state: ResolverState<any>,
    changeContext: ChangeContext,
    dataChanged: boolean
  ) => void;
  onNodeCollected: (instanceKey: string) => void;
  devtools?: DevtoolsCallbacks;
  persistenceManager?: PersistenceManager;
}) => {
  logger?.debug('startup');

  const nodeStore = createNodeStore({ devtools });

  let active = false;
  let stateEffects: DeferredLifecycleEffect[] = [];
  let invalidationEffects: DeferredLifecycleEffect[] = [];
  let refetchEffects: DeferredLifecycleEffect[] = [];

  const enqueueStateEffect = (effect: DeferredLifecycleEffect) => {
    if (active) {
      stateEffects.push(effect);
      return;
    }

    effect();
  };

  const enqueueInvalidationEffect = (effect: DeferredLifecycleEffect) => {
    if (active) {
      invalidationEffects.push(effect);
      return;
    }

    effect();
  };

  const dispatchInvalidation = (
    instanceKey: string,
    changeContext: ChangeContext
  ) => {
    if (active) {
      refetchEffects.push(() => {
        onInvalidate({ instanceKey, triggeringContext: changeContext });
      });
      return;
    }

    onInvalidate({ instanceKey, triggeringContext: changeContext });
  };

  const deferLifecycleEffects = (callback: () => void) => {
    // Re-entrant: a wavefront is already draining. Run inline so anything the
    // callback schedules lands on the active tiers and drains in order at the
    // root.
    if (active) {
      callback();
      return;
    }

    active = true;
    stateEffects = [];
    invalidationEffects = [];
    refetchEffects = [];

    try {
      callback();

      // Prioritized drain: state, then invalidation marking, then refetch.
      //
      // State is the stronger fact — a value that actually arrived this wave —
      // so all state propagation reaches fixpoint before any invalidation is
      // applied. The originId guard in tryMarkStale then drops any same-origin
      // invalidation of a node that already received state: it never marks stale
      // and never dispatches a refetch. That makes "state trumps invalidation"
      // hold regardless of graph shape or the order paths happen to arrive,
      // rather than first-effect-wins.
      //
      // Invalidation marking drains as its own tier before refetches, so every
      // linked node in the invalidation front is marked stale before any node's
      // refetch is dispatched. Within a tier, effects append to the tail and
      // drain breadth-first so the changeId guard can't freeze a node on a
      // partial value from a longer path. Higher tiers always drain first, so a
      // synchronous refetch that feeds new state back in is applied before the
      // next lower-tier effect runs.
      let stateIndex = 0;
      let invalidationIndex = 0;
      let refetchIndex = 0;
      while (
        stateIndex < stateEffects.length ||
        invalidationIndex < invalidationEffects.length ||
        refetchIndex < refetchEffects.length
      ) {
        if (stateIndex < stateEffects.length) {
          stateEffects[stateIndex++]();
          continue;
        }
        if (invalidationIndex < invalidationEffects.length) {
          invalidationEffects[invalidationIndex++]();
          continue;
        }
        refetchEffects[refetchIndex++]();
      }
    } finally {
      active = false;
      stateEffects = [];
      invalidationEffects = [];
      refetchEffects = [];
    }
  };

  const onChange = (
    instanceKey: string,
    entry: CacheEntry<any, any>,
    { dataChanged, flagsChanged }: ChangeMeta
  ) => {
    if (entry.state.error) {
      setResourceErrorMeta(entry.state.error, {
        typeName: parseTypeName(instanceKey),
        instanceKey,
        params: entry.meta.params,
      });
    }

    onStateChange(
      instanceKey,
      entry.state,
      entry.meta.changeContext,
      dataChanged
    );

    const resource = resourceRegistry.getResource(instanceKey);

    if (resource.persist && dataChanged) {
      persistenceManager?.write(instanceKey, entry.state);
    }

    if (resource.onChange) {
      queueMicrotask(() => {
        try {
          resource.onChange?.({
            state: entry.state,
            params: entry.meta.params,
            dataChanged,
            flagsChanged,
          });
        } catch (error) {
          logger?.error('error calling onChange', {
            error: (error as Error).message,
          });
        }
      });
    }
  };

  const linkManager = createLinkManager({
    nodeStore,
    resourceRegistry,
    onInvalidated: dispatchInvalidation,
    onChange,
    schedule: enqueueStateEffect,
    scheduleInvalidation: enqueueInvalidationEffect,
    logger,
    devtools,
  });

  const syncTags = linkManager.syncTags;

  const collectNode = (instanceKey: string, changeContext: ChangeContext) => {
    const entry = nodeStore.get(instanceKey);

    if (!entry) {
      return;
    }

    if (entry.meta.changeContext.changeId >= changeContext.changeId) {
      return;
    }

    const result: ChangeMeta = { dataChanged: false, flagsChanged: false };

    nodeStore.getEmitter(instanceKey).emit('pull', { changeContext, result });

    if (!result.dataChanged && !result.flagsChanged) {
      return;
    }

    const merged = nodeStore.get(instanceKey)!;

    if (result.dataChanged && !changeContext.optimistic) {
      syncTags(instanceKey, changeContext);
    }

    merged.meta.lastChange = result;

    onChange(instanceKey, merged, result);

    enqueueStateEffect(() =>
      nodeStore.getEmitter(instanceKey).emit('state', {
        changeContext,
        dataChanged: result.dataChanged,
        flagsChanged: result.flagsChanged,
      })
    );
  };

  const gcManager = createGCManager({
    nodes: nodeStore.nodes,
    removeNode: (instanceKey: string) => {
      linkManager.removeInstance(instanceKey, changeManager.start('gc'));
      nodeStore.remove(instanceKey);

      groupManager.remove(parseTypeName(instanceKey), instanceKey);

      onNodeCollected(instanceKey);
    },
    logger: logger?.withTags({ component: 'gcManager' }),
  });

  const invalidateNode = (
    instanceKey: string,
    changeContext: ChangeContext
  ) => {
    const entry = nodeStore.get(instanceKey);
    if (!entry) {
      return;
    }

    const { hash } = resourceRegistry.getResource(instanceKey);
    const nextEntry = tryMarkStale(entry, hash, changeContext);

    if (!nextEntry) {
      return;
    }

    nodeStore.set(instanceKey, nextEntry);

    onChange(instanceKey, nextEntry, {
      dataChanged: false,
      flagsChanged: true,
    });

    devtools?.trace({
      changeContext,
      instanceKey,
      parent: 'origin',
      type: 'invalidation',
    });

    dispatchInvalidation(instanceKey, changeContext);

    nodeStore.getEmitter(instanceKey).emit('invalidation', { changeContext });
  };

  const registerNode = ({
    instanceKey,
    params,
    changeContext,
    registerLogger,
  }: {
    params: any;
    instanceKey: string;
    changeContext: ChangeContext;
    registerLogger?: Logger;
  }): {
    entry: CacheEntry<any, any>;
    didCreate: boolean;
    wasVisible: boolean;
  } => {
    const existing = nodeStore.get(instanceKey);
    if (existing) {
      return {
        entry: existing,
        didCreate: false,
        wasVisible: existing.meta.visible,
      };
    }

    registerLogger?.debug('creating node');

    const typeName = parseTypeName(instanceKey);

    groupManager.add(typeName, instanceKey);

    const { tags: paramLinkTags } = calculateTags({
      typeName,
      params,
      resourceRegistry,
    });

    const newNode = createEmptyEntry(params, paramLinkTags);
    const emitter = nodeStore.create(instanceKey, newNode);

    emitter.on('collect', ({ changeContext: collectContext }) =>
      collectNode(instanceKey, collectContext)
    );

    // Intentionally not reporting a trace edge or state change here. Node registration
    // is a multi-step process — we first set up the tag registry and reachable
    // instances by params, then attempt to initialize via links and refresh
    // if not able to initialize. The trace should only be reported after all is said and done.
    devtools?.onNodeAdded(instanceKey, changeContext);
    syncTags(instanceKey, changeContext);

    return { entry: newNode, didCreate: true, wasVisible: false };
  };

  const requestReplay = (instanceKey: string, initLogger?: Logger) => {
    const priorContext = nodeStore.get(instanceKey)?.meta.changeContext;

    let parent: string | null = null;

    nodeStore.getEmitter(instanceKey).emit('init', {
      onInit: (parentInstanceKey) => {
        parent = parentInstanceKey;
      },
    });

    const entry = nodeStore.get(instanceKey);

    if (entry && entry.meta.changeContext !== priorContext) {
      initLogger?.info('initialized from links');

      syncTags(instanceKey, entry.meta.changeContext);

      return [entry, parent!] as const;
    }

    return [null, null] as const;
  };

  const isSettled = (entry: CacheEntry<any, any> | undefined) =>
    !!entry?.meta.visible &&
    !entry.state.loading &&
    (!entry.state.stale || !!entry.state.error) &&
    entry.state.data !== undefined;

  const isFailed = (entry: CacheEntry<any, any> | undefined) =>
    !!entry?.meta.visible &&
    !entry.state.loading &&
    !!entry.state.error &&
    entry.state.data === undefined;

  return {
    resync: ({
      added,
      removed,
      changeContext,
    }: {
      added: Link[] | null;
      removed: Link[] | null;
      changeContext: ChangeContext;
    }) => {
      timing.timeResync(() => {
        const affectedTypes = new Set<string>();

        for (const link of [...(added ?? []), ...(removed ?? [])]) {
          affectedTypes.add(link.selfTypeName);
          affectedTypes.add(link.otherTypeName);
        }

        // First pass: recalculate param tags (link definitions may have changed)
        for (const typeName of affectedTypes) {
          const instances = groupManager.get(typeName);
          if (instances) {
            for (const instanceKey of [...instances]) {
              const node = nodeStore.get(instanceKey);
              if (node) {
                nodeStore.patch(instanceKey, {
                  ...node,
                  meta: {
                    ...node.meta,
                    paramLinkTags: calculateTags({
                      typeName,
                      params: node.meta.params,
                      resourceRegistry,
                    }).tags,
                  },
                });
              }
            }
          }
        }

        // Second pass: nuke + rebuild tags for all affected instances
        for (const typeName of affectedTypes) {
          const instances = groupManager.get(typeName);
          if (instances) {
            for (const instanceKey of instances) {
              syncTags(instanceKey, changeContext);
            }
          }
        }
      });
    },
    isAttached: (instanceKey: string) =>
      nodeStore.get(instanceKey)?.lifecycle.attachedSince != null,
    getParams: (instanceKey: string) => nodeStore.get(instanceKey)?.meta.params,
    getStoredAt: (instanceKey: string) =>
      nodeStore.get(instanceKey)?.meta.storedAt,
    isSettled: (instanceKey: string) => isSettled(nodeStore.get(instanceKey)),
    isExpired: (instanceKey: string, staleTime: number) => {
      const entry = nodeStore.get(instanceKey);
      const since = isSettled(entry)
        ? entry!.meta.settledAt ?? entry!.meta.storedAt ?? 0
        : isFailed(entry)
        ? entry!.meta.failedAt
        : undefined;

      return since !== undefined && Date.now() - since >= staleTime;
    },
    getState: <TData>(instanceKey: string) => {
      const entry = nodeStore.get(instanceKey) as
        | CacheEntry<any, TData>
        | undefined;
      return entry?.meta.visible ? entry.state : undefined;
    },
    materialize: <TData>({
      instanceKey,
      params,
      changeContext,
    }: {
      instanceKey: string;
      params: any;
      changeContext: ChangeContext;
    }): ResolverState<TData> | undefined => {
      return timing.timeMaterialize(() => {
        const materializeLogger = logger?.withTags({
          instanceKey,
          changeId: changeContext.changeId,
          originId: changeContext.originId,
        });

        materializeLogger?.debug('materializing node');

        const { entry: node, didCreate } = registerNode({
          instanceKey,
          params,
          changeContext,
          registerLogger: materializeLogger,
        });

        if (!didCreate) {
          return node.meta.visible ? node.state : undefined;
        }

        const [initialized] = requestReplay(instanceKey, materializeLogger);

        gcManager.queue(instanceKey, -1);

        if (initialized) {
          onChange(instanceKey, initialized, {
            dataChanged: true,
            flagsChanged: true,
          });

          return initialized.state;
        }

        nodeStore.patch(instanceKey, {
          ...node,
          meta: {
            ...node.meta,
            visible: false,
          },
        });

        return undefined;
      });
    },
    attach: ({
      instanceKey,
      params,
      changeContext,
    }: {
      instanceKey: string;
      params: any;
      changeContext: ChangeContext;
    }) => {
      timing.timeAttach(() => {
        const attachLogger = logger?.withTags({
          instanceKey,
          changeId: changeContext.changeId,
          originId: changeContext.originId,
        });

        attachLogger?.info('attaching resource');

        const {
          entry: node,
          didCreate,
          wasVisible,
        } = registerNode({
          instanceKey,
          params,
          changeContext,
          registerLogger: attachLogger,
        });

        node.lifecycle.attachedSince = Date.now();

        nodeStore.patch(instanceKey, {
          ...node,
          meta: {
            ...node.meta,
            visible: true,
          },
        });

        // If the node is new to the cache or was previously materialized without
        // data, attempt to populate it from links.
        if (didCreate || !wasVisible) {
          attachLogger?.debug('attempting to populate from links');

          const [initialized, parent] = requestReplay(
            instanceKey,
            attachLogger
          );

          if (initialized) {
            attachLogger?.info('initial state populated', { parent });

            devtools?.traceInit(parent, instanceKey, changeContext);
          }
        }

        const finalizedNode = nodeStore.get(instanceKey)!;
        const { stale, loading } = finalizedNode.state;

        // If the node is resolved and still stale/loading
        // after population, we should attempt to grab a fresh copy
        if (stale || loading) {
          attachLogger?.debug('initial state requires invalidation');

          devtools?.trace({
            changeContext,
            instanceKey,
            parent: 'origin',
            type: 'invalidation',
          });

          onInvalidate({
            instanceKey,
            triggeringContext: changeContext,
          });
        }

        onChange(instanceKey, nodeStore.get(instanceKey)!, {
          dataChanged: true,
          flagsChanged: true,
        });
      });
    },
    detach: (instanceKey: string) => {
      timing.timeDetach(() => {
        logger?.debug('detaching resource', { instanceKey });

        const node = nodeStore.get(instanceKey);

        if (!node) {
          logger?.debug('node not found, skipping gc', { instanceKey });
          return;
        }

        // Intentionally not reporting a state change here (for now), as it
        // would replace the prior state in history w/ same changeId.
        // TODO: Consider providing detaches with a changeId? Would mess
        // with post-detach propagation
        const attachedDuration =
          Date.now() - (node.lifecycle.attachedSince ?? 0);

        node.lifecycle.attachedSince = undefined;

        logger?.debug('queueing gc', {
          instanceKey,
          duration: attachedDuration,
        });

        gcManager.queue(instanceKey, attachedDuration);
      });
    },
    retain: ({
      instanceKey,
      params,
      period,
    }: {
      instanceKey: string;
      params: any;
      period?: number;
    }) => {
      if (!nodeStore.get(instanceKey)) {
        const changeContext = changeManager.start('retain');

        const retainLogger = logger?.withTags({
          instanceKey,
          changeId: changeContext.changeId,
          originId: changeContext.originId,
        });

        retainLogger?.debug('reserving node for retention');

        const { entry } = registerNode({
          instanceKey,
          params,
          changeContext,
          registerLogger: retainLogger,
        });

        nodeStore.patch(instanceKey, {
          ...entry,
          meta: {
            ...entry.meta,
            visible: false,
          },
        });
      }

      gcManager.retain(instanceKey, period);
    },
    invalidate: (instanceKey: string, changeContext: ChangeContext) => {
      timing.timeInvalidate(() => {
        deferLifecycleEffects(() => {
          invalidateNode(instanceKey, changeContext);
        });
      });
    },
    invalidateAll: (typeName: string, changeContext: ChangeContext) => {
      timing.timeInvalidateAll(() => {
        const instances = groupManager.get(typeName);

        if (instances) {
          deferLifecycleEffects(() => {
            for (const instanceKey of instances) {
              invalidateNode(instanceKey, changeContext);
            }
          });
        }
      });
    },
    handleStateChange: (
      sourceInstanceKey: string,
      params: any,
      changeContext: ChangeContext,
      nextState: ResolverState<any>,
      storedAt?: number
    ) => {
      timing.timeStateChange(() => {
        const stateChangeLogger = logger?.withTags({
          sourceInstanceKey,
          instanceKey: sourceInstanceKey,
          changeId: changeContext.changeId,
          originId: changeContext.originId,
        });

        stateChangeLogger?.info('handling state change', {
          canCascade: changeContext.canCascade,
        });

        const { entry, didCreate } = registerNode({
          instanceKey: sourceInstanceKey,
          params,
          changeContext,
          registerLogger: stateChangeLogger?.withTags({
            component: 'registerNode',
          }),
        });

        if (didCreate) {
          gcManager.queue(sourceInstanceKey, -1);
        }

        if (entry.meta.changeContext.changeId >= changeContext.changeId) {
          return;
        }

        const { hash } = resourceRegistry.getResource(sourceInstanceKey);

        const result = produceNextCacheEntry({
          hash,
          currentEntry: entry,
          params: entry.meta.params,
          changeContext,
          nextState,
          storedAt,
        });

        const { state } = result.nextEntry;
        const timestamp =
          changeContext.optimistic || state.loading
            ? undefined
            : !state.stale && !state.error && state.data !== undefined
            ? {
                settledAt: Math.max(
                  entry.meta.settledAt ?? 0,
                  storedAt ?? Date.now()
                ),
              }
            : state.error && state.data === undefined
            ? { failedAt: storedAt ?? Date.now() }
            : undefined;

        if (timestamp) {
          if (result.nextEntry === entry) {
            nodeStore.patch(sourceInstanceKey, {
              ...entry,
              meta: { ...entry.meta, ...timestamp },
            });
          } else {
            Object.assign(result.nextEntry.meta, timestamp);
          }
        }

        if (!result.dataChanged && !result.flagsChanged) {
          if (didCreate) {
            onStateChange(
              sourceInstanceKey,
              entry.state,
              entry.meta.changeContext,
              false
            );
          }
          return;
        }

        devtools?.trace({
          parent: 'origin',
          type: 'state',
          changeContext,
          instanceKey: sourceInstanceKey,
        });

        result.nextEntry.meta.lastChange = {
          dataChanged: result.dataChanged,
          flagsChanged: result.flagsChanged,
        };

        nodeStore.set(sourceInstanceKey, result.nextEntry);

        if (result.dataChanged && !changeContext.optimistic) {
          syncTags(sourceInstanceKey, changeContext);
        }

        onChange(sourceInstanceKey, result.nextEntry, {
          dataChanged: result.dataChanged,
          flagsChanged: result.flagsChanged,
        });

        deferLifecycleEffects(() => {
          nodeStore.getEmitter(sourceInstanceKey).emit('state', {
            changeContext,
            dataChanged: result.dataChanged,
            flagsChanged: result.flagsChanged,
          });
        });
      });
    },
    getInstancesForType: <TParams, TData, TError extends Error = Error>(
      typeName: string
    ) => {
      const instances = groupManager.get(typeName);
      if (!instances) return [];
      const results: ResourceInstanceState<TParams, TData, TError>[] = [];
      for (const instanceKey of instances) {
        const entry = nodeStore.get(instanceKey);
        if (entry?.meta.visible) {
          results.push({
            instanceKey,
            params: entry.meta.params,
            state: entry.state as ResolverState<TData, TError>,
          });
        }
      }
      return results;
    },
    removeAllOfType: (typeName: string, changeContext: ChangeContext) => {
      const instances = groupManager.get(typeName);

      if (!instances) {
        return;
      }

      for (const instanceKey of instances.values()) {
        linkManager.removeInstance(instanceKey, changeContext);
        nodeStore.remove(instanceKey);
        groupManager.remove(typeName, instanceKey);
        onNodeCollected(instanceKey);
      }
    },
    getAllGCStatuses: () => gcManager.getAllStatuses(),
  };
};

export type ResourceLifecycleManager = ReturnType<
  typeof createResourceLifecycleManager
>;
